import type { ScheduleEvent, ScheduleOwner, WeddingData } from '@/mock/types'

export type ScheduleFilter = 'all' | 'guests' | 'team' | 'untimed' | 'conflicts'

export const scheduleFilters: ScheduleFilter[] = ['all', 'guests', 'team', 'untimed', 'conflicts']

export const filterLabels: Record<ScheduleFilter, string> = {
  all: 'Vše',
  guests: 'Pro hosty',
  team: 'Jen tým',
  untimed: 'Bez času',
  conflicts: 'Kolize',
}

/** What sits between an event and the one before it on the day. */
export type Gap =
  | { type: 'free'; minutes: number }
  | { type: 'overlap'; with: ScheduleEvent }
  | { type: 'order'; with: ScheduleEvent }

// The day runs past midnight: times before 05:00 belong to the night after.
const dayStart = 5 * 60

export function minutes(time: string) {
  const [hours = 0, mins = 0] = time.split(':').map(Number)
  const value = hours * 60 + mins
  return value < dayStart ? value + 24 * 60 : value
}

function endMinutes(event: ScheduleEvent) {
  if (!event.time) return null
  if (!event.endTime) return minutes(event.time)
  const end = minutes(event.endTime)
  return end < minutes(event.time) ? end + 24 * 60 : end
}

export function timeRange(event: ScheduleEvent) {
  if (!event.time) return null
  return event.endTime ? `${event.time}–${event.endTime}` : event.time
}

export function formatDuration(total: number) {
  const hours = Math.floor(total / 60)
  const rest = total % 60
  if (!hours) return `${rest} min`
  return rest ? `${hours} h ${rest} min` : `${hours} h`
}

export type ScheduleGroup = { id: string | null; label: string; events: ScheduleEvent[] }

/** Preparations first, then the parts of the day from Hosté → Organizace, in their order. */
export function scheduleGroups(data: WeddingData): ScheduleGroup[] {
  const active = data.schedule.filter((event) => !event.removed)
  const known = new Set(data.dayParts.map((part) => part.id))
  return [
    {
      id: null,
      label: 'Přípravy',
      events: active.filter((event) => !event.dayPartId || !known.has(event.dayPartId)),
    },
    ...data.dayParts.map((part) => ({
      id: part.id,
      label: part.label,
      events: active.filter((event) => event.dayPartId === part.id),
    })),
  ]
}

export function groupRange(events: ScheduleEvent[]) {
  const timed = events.filter((event) => event.time)
  if (!timed.length) return null
  const start = Math.min(...timed.map((event) => minutes(event.time as string)))
  const end = Math.max(...timed.map((event) => endMinutes(event) as number))
  return start === end ? clock(start) : `${clock(start)}–${clock(end)}`
}

/** Minutes since midnight as "HH:mm", wrapping past midnight. */
export function clock(value: number) {
  const wrapped = value % (24 * 60)
  return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`
}

/** Two events need the same people: both are for guests, or the same member or supplier handles both. */
function samePeople(a: ScheduleEvent, b: ScheduleEvent) {
  if (a.audience === 'guests' && b.audience === 'guests') return true
  return Boolean(a.owner && b.owner && a.owner.type === b.owner.type && a.owner.id === b.owner.id)
}

/**
 * The gap before each timed event, walking the whole day in the couple's
 * order: a time earlier than the previous event (out of order), a clash with
 * a still-running event that needs the same people, or 45+ minutes of free
 * time. A short event inside a long one for other people (a surprise during
 * the party) is fine.
 */
export function scheduleGaps(groups: ScheduleGroup[]) {
  const gaps = new Map<string, Gap>()
  const earlier: ScheduleEvent[] = []
  for (const event of groups.flatMap((group) => group.events)) {
    if (!event.time) continue
    const start = minutes(event.time)
    const previous = earlier.at(-1)
    const clash = earlier.find(
      (other) =>
        minutes(other.time as string) <= start &&
        start < (endMinutes(other) as number) &&
        samePeople(other, event),
    )
    const latestEnd = Math.max(...earlier.map((other) => endMinutes(other) as number))
    if (previous?.time && start < minutes(previous.time))
      gaps.set(event.id, { type: 'order', with: previous })
    else if (clash) gaps.set(event.id, { type: 'overlap', with: clash })
    else if (earlier.length && start - latestEnd >= 45)
      gaps.set(event.id, { type: 'free', minutes: start - latestEnd })
    earlier.push(event)
  }
  return gaps
}

export function isConflict(gap: Gap | undefined) {
  return gap?.type === 'overlap' || gap?.type === 'order'
}

export function matchesFilter(
  event: ScheduleEvent,
  filter: ScheduleFilter,
  gaps: Map<string, Gap>,
) {
  if (filter === 'guests') return event.audience === 'guests'
  if (filter === 'team') return event.audience === 'team'
  if (filter === 'untimed') return !event.time
  if (filter === 'conflicts') return isConflict(gaps.get(event.id))
  return true
}

/** Sort each part of the day by time; events without a time go last. Removed events keep their place. */
export function sortByTime(schedule: ScheduleEvent[]) {
  const key = (event: ScheduleEvent) => (event.time ? minutes(event.time) : Number.MAX_SAFE_INTEGER)
  const sorted = schedule.filter((event) => !event.removed).sort((a, b) => key(a) - key(b))
  let index = 0
  return schedule.map((event) => (event.removed ? event : (sorted[index++] ?? event)))
}

/** Move `activeId` to where `overId` is (drag and drop, "Posunout výše/níže"). */
export function moveEvent(schedule: ScheduleEvent[], activeId: string, overId: string) {
  const from = schedule.findIndex((event) => event.id === activeId)
  const to = schedule.findIndex((event) => event.id === overId)
  if (from < 0 || to < 0 || from === to) return schedule
  const next = [...schedule]
  const [moved] = next.splice(from, 1)
  if (moved) next.splice(to, 0, moved)
  return next
}

export function ownerOf(owner: ScheduleOwner | undefined, data: WeddingData) {
  if (!owner) return null
  if (owner.type === 'member') {
    const member = data.members.find((item) => item.id === owner.id)
    return member ? { type: 'member' as const, name: member.name, initials: member.initials } : null
  }
  const supplier = data.suppliers.find((item) => item.id === owner.id)
  return supplier ? { type: 'supplier' as const, name: supplier.name } : null
}

/** Everyone who handles at least one event, for the "Zajišťuje" filter. */
export function scheduleOwners(data: WeddingData) {
  const seen = new Map<string, { key: string; name: string }>()
  for (const event of data.schedule) {
    if (event.removed || !event.owner) continue
    const key = `${event.owner.type}:${event.owner.id}`
    const owner = ownerOf(event.owner, data)
    if (owner && !seen.has(key)) seen.set(key, { key, name: owner.name })
  }
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name, 'cs'))
}

export function ownerKey(event: ScheduleEvent) {
  return event.owner ? `${event.owner.type}:${event.owner.id}` : null
}

/** The part of the day an event at `time` most likely belongs to: the last one that has started by then. */
export function suggestDayPart(groups: ScheduleGroup[], time: string) {
  const at = minutes(time)
  let suggestion: string | null = null
  for (const group of groups) {
    const starts = group.events
      .filter((event) => event.time)
      .map((event) => minutes(event.time as string))
    if (starts.length && Math.min(...starts) <= at) suggestion = group.id
  }
  return suggestion
}

/**
 * Save an event into the schedule. An unchanged time and part of the day keep
 * the couple's order; otherwise it slots into its part of the day by time
 * (untimed events go last).
 */
export function placeEvent(schedule: ScheduleEvent[], event: ScheduleEvent) {
  const existing = schedule.find((item) => item.id === event.id)
  if (existing && existing.time === event.time && existing.dayPartId === event.dayPartId)
    return schedule.map((item) => (item.id === event.id ? event : item))
  const rest = schedule.filter((item) => item.id !== event.id)
  const sameGroup = rest
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => !item.removed && item.dayPartId === event.dayPartId)
  if (!sameGroup.length) return [...rest, event]
  const at = event.time ? minutes(event.time) : Number.POSITIVE_INFINITY
  const before = sameGroup.filter(({ item }) => item.time && minutes(item.time) <= at)
  const index = before.length
    ? (before.at(-1)?.index ?? rest.length) + 1
    : event.time
      ? (sameGroup[0]?.index ?? rest.length)
      : (sameGroup.at(-1)?.index ?? rest.length) + 1
  return [...rest.slice(0, index), event, ...rest.slice(index)]
}

/** How long an event runs, or that it ends after midnight. */
export function durationLabel(time: string | null, endTime: string | null) {
  if (!time || !endTime) return null
  const start = minutes(time)
  let end = minutes(endTime)
  if (end < start) end += 24 * 60
  if (end === start) return null
  return `Trvá ${formatDuration(end - start)}`
}

export const wholeDay = 'all'

/** Who the preview is for: the whole day, or the parts of the day one invitation covers. */
export type GuestAudience = { key: string; label: string; parts: string[]; invitations: number }

/**
 * "Celý den", then every combination of parts that an invitation in Hosté
 * actually has, then each part on its own, so the couple can check any guest.
 */
export function guestAudiences(data: WeddingData): GuestAudience[] {
  const all = data.dayParts.map((part) => part.id)
  const keyOf = (parts: string[]) =>
    parts.length === all.length ? wholeDay : all.filter((id) => parts.includes(id)).join('+')
  const labelOf = (parts: string[]) =>
    parts.length === all.length
      ? 'Celý den'
      : `Jen ${data.dayParts
          .filter((part) => parts.includes(part.id))
          .map((part) => part.label.toLowerCase())
          .join(' a ')}`
  const audiences = new Map<string, GuestAudience>()
  const add = (parts: string[], invitations = 0) => {
    const key = keyOf(parts)
    const existing = audiences.get(key)
    if (existing) existing.invitations += invitations
    else audiences.set(key, { key, label: labelOf(parts), parts, invitations })
  }
  add(all)
  for (const party of data.parties) add(party.parts, 1)
  for (const part of all) add([part])
  return [...audiences.values()]
}

/** What a supplier's copy depends on; a different value means the schedule changed since it was sent. */
export function scheduleSignature(schedule: ScheduleEvent[]) {
  return JSON.stringify(
    schedule
      .filter((event) => !event.removed)
      .map(({ id, time, endTime, title, location, description, owner }) => [
        id,
        time,
        endTime,
        title,
        location,
        description,
        owner?.id,
      ]),
  )
}
